import type { TvChannel } from '@setplex/tria-api'
import type { EpgRequestParams, Range } from '../index.h'

import { A } from '@mobily/ts-belt'
import { idEq } from '~/shared/helpers'
import { NO_EPG_ID } from '../index.h'
import { biggestCoverRange, unloadedRanges } from './ranges'

export const narrowEpgRequestToNecessary = ({
  requested,
  loaded,
}: {
  requested: EpgRequestParams
  loaded: ReadonlyMap<string, Range[]>
}): EpgRequestParams => {
  const channels = requested.channels.slice()
  const periods: Range[] = []

  // exclude channels, epg for which is already loaded for requested period
  for (const { id, epgId = NO_EPG_ID } of requested.channels) {
    const ranges = loaded.get(epgId) ?? []
    const unloaded = unloadedRanges(ranges, requested.period)

    // if there are no unloaded ranges, then we don't need to fetch epg for that channel
    if (A.isEmpty(unloaded)) {
      const notNeededIndex = channels.findIndex(idEq(id))
      if (notNeededIndex !== -1) {
        channels.splice(notNeededIndex, 1)
      }
    }

    // otherwise, we need to fetch epg for that channel
    else {
      periods.push(...unloaded)
    }
  }

  return {
    channels, // it is ok, if we'll come to empty array here, it will be filtered out
    period: biggestCoverRange(periods),
  }
}

export const formatEpgRequestToFetchParams = ({
  channels,
  period,
}: EpgRequestParams) => ({
  epgIds: A.uniq(A.map(channels, ({ epgId }) => epgId ?? NO_EPG_ID)),
  channels,
  period: { start: period![0], end: period![1] },
})

const getUniqueEpgIds = (channels: TvChannel[]) =>
  A.uniq(A.map(channels, ({ epgId }) => epgId ?? NO_EPG_ID))

export const markChannelsEpgAsPending = (
  [pending]: [Map<string, number>],
  { channels }: { channels: TvChannel[] }
): [Map<string, number>] => {
  for (const id of getUniqueEpgIds(channels)) {
    pending.set(id, (pending.get(id) ?? 0) + 1)
  }

  return [pending]
}

export const unmarkChannelsEpgAsPending = (
  [pending]: [Map<string, number>],
  { params: { channels } }: { params: { channels: TvChannel[] } }
): [Map<string, number>] => {
  for (const id of getUniqueEpgIds(channels)) {
    const count = (pending.get(id) ?? 0) - 1

    if (count > 0) {
      pending.set(id, count)
    } else {
      pending.delete(id)
    }
  }

  return [pending]
}
